import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthProvider.js";
import { ApiFetchError } from "../../lib/api.js";
import { Badge, Button, Card, SelectionCard } from "../../components/index.js";
import type {
  PlacementOption,
  TransportationOption,
  WorkflowStatusInfo
} from "../../types.js";
import {
  buildCarrierCoverageSelections,
  carrierDisplayName,
  cheapestPartnered,
  computeBreakdown,
  decideStep3Phase,
  decideTransportPoll,
  formatMoney,
  isOwnCarrier,
  ownCarrierCount,
  partneredOptions,
  placementDiscountAmount,
  placementFeeAmount,
  placementNetCost,
  POST_TRANSPORT_STAGES,
  recommendedPlacement,
  selectableOwnCarrierOptions,
  shippingModeLabel,
  TERMINAL_WORKFLOW_STATUSES
} from "../../lib/step3-transport.js";
import type { StepProps } from "./step-props.js";

/**
 * Etapa 3 — Confirmação de envio (Design.md §6.3). Esta etapa DIRIGE a metade
 * traseira do workflow durable (antes só lia, e por isso ficava sempre vazia):
 *
 *  1. o plano chega aqui parado em WAIT_FOR_PLACEMENT_OPTION;
 *  2. seleciona a opção de posicionamento (placement) → o workflow gera as opções
 *     de transporte (GENERATE/LIST_TRANSPORTATION) e para em
 *     WAIT_FOR_TRANSPORTATION_OPTIONS;
 *  3. lista as transportadoras PARCEIRAS da Amazon (SPD) com suas cotações e o
 *     "Total estimado" (taxa de posicionamento + frete);
 *  4. com o aceite, envia a seleção (transportation-option-selected) → o workflow
 *     confirma posicionamento + transporte e finaliza (etiquetas de caixa).
 *
 * As mutações de placement/transporte são ADMIN-only no backend (RBAC); 403 é
 * exibido com clareza para operadores.
 */
const POLL_MS = 3000;
const MAX_POLLS = 40; // ~2 min
/**
 * Busca de transportadora tem orçamento MAIOR: o workflow pode sondar VÁRIAS
 * opções de posicionamento em busca de parceira (placement-partnered-probe),
 * cada uma custando um ciclo generate→poll→list na Amazon (~40 s observados em
 * produção). 2 min estouravam com 3+ placements; ~5 min cobre com folga.
 */
const TRANSPORT_MAX_POLLS = 100; // ~5 min

type Phase =
  | "loading"
  // O plano ainda está progredindo no backend até a placement (Passo 2 → 3):
  // pollamos em vez de dar erro. Antes essa situação travava em "Conclua o Passo 2".
  | "preparing"
  | "need-placement"
  | "generating"
  | "choose-carrier"
  | "no-carriers"
  | "delivery-window"
  | "confirmed"
  | "terminal"
  | "error";

interface Snapshot {
  status: WorkflowStatusInfo;
  placement: PlacementOption[];
  /** Placement REALMENTE em uso no backend (pode ter sido re-selecionada). */
  selectedPlacementOptionId: string | null;
  transport: TransportationOption[];
  /**
   * Backend ainda procurando transportadora parceira (sondando outra placement).
   * Ausente em API anterior a este contrato ⇒ false (gate fica só no estágio).
   */
  partneredSearching: boolean;
  /** Progresso da sonda (placements já testadas / total), para a mensagem. */
  partneredProbed: number;
  partneredTotal: number;
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Data de hoje (YYYY-MM-DD, local) — default da data de envio. */
function todayIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function Step3Confirmacao({ planId, onReadyChange }: StepProps): ReactNode {
  const { api } = useAuth();
  const [phase, setPhase] = useState<Phase>("loading");
  const [placementOptions, setPlacementOptions] = useState<PlacementOption[]>([]);
  const [selectedPlacementId, setSelectedPlacementId] = useState<string | null>(null);
  // Placement que o BACKEND está usando de fato: ao procurar transportadora
  // parceira, o workflow pode re-selecionar outra opção de posicionamento (a
  // oferta de parceira depende dela). Sem isto a UI mostraria a taxa da opção
  // que o operador clicou, não a do envio real.
  const [effectivePlacementId, setEffectivePlacementId] = useState<string | null>(
    null
  );
  const [transport, setTransport] = useState<TransportationOption[]>([]);
  const [selectedTransportId, setSelectedTransportId] = useState<string | null>(null);
  const [aceite, setAceite] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Motivo REAL da falha + saída, no mesmo padrão do Passo 2: sem isto a tela
  // dizia só "cancelado ou falhou — crie um novo plano", escondendo a causa que
  // o backend já expõe em `failureReason` (e sem nenhuma ação possível).
  const [failureReason, setFailureReason] = useState<string | null>(null);
  const [failureSkus, setFailureSkus] = useState<string[]>([]);
  const [canRestart, setCanRestart] = useState(false);
  const [recuperando, setRecuperando] = useState(false);
  const [erroRecuperacao, setErroRecuperacao] = useState<string | null>(null);
  // Data de envio (pronto p/ coleta da transportadora) — vira readyToShipWindow.start
  // no generateTransportationOptions. Default: hoje. O operador ajusta antes de buscar
  // as transportadoras (a data é fixada quando as opções de transporte são geradas).
  const [shipDate, setShipDate] = useState<string>(todayIso);
  const alive = useRef(true);
  const placementSent = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Prontidão para "Avançar" = transporte confirmado (workflow seguiu p/ finalize).
  useEffect(() => {
    onReadyChange(phase === "confirmed");
  }, [phase, onReadyChange]);

  // Na fase de escolha do posicionamento vale o clique do operador; depois de
  // gerado o transporte vale a placement EFETIVA do backend.
  const selectedPlacement =
    placementOptions.find(
      (o) =>
        o.placementOptionId === (effectivePlacementId ?? selectedPlacementId)
    ) ??
    recommendedPlacement(placementOptions);
  // O backend trocou a opção de posicionamento para achar transportadora parceira?
  const placementTrocada =
    effectivePlacementId !== null &&
    selectedPlacementId !== null &&
    effectivePlacementId !== selectedPlacementId;
  // Opções selecionáveis: PARCEIRAS da Amazon (recomendadas, com cotação) +
  // TRANSPORTADORAS PRÓPRIAS reconhecíveis (DPD/UPS/… — para Hazmat ou quando a
  // Amazon não ofereceu parceira). Antes só as parceiras eram selecionáveis e, sem
  // parceira, a tela era um beco sem saída.
  const partnered = partneredOptions(transport);
  const ownCarriers = selectableOwnCarrierOptions(transport);
  const selectedCarrier =
    [...partnered, ...ownCarriers].find(
      (o) => o.transportationOptionId === selectedTransportId
    ) ?? null;
  const selectedIsOwn = selectedCarrier !== null && isOwnCarrier(selectedCarrier);
  // Cobertura total (§9.5): UMA seleção por shipment, todas com o carrier escolhido.
  // Calculada já na renderização para que o "Total estimado" some o transporte de
  // TODOS os shipments (não só o selecionado) antes do aceite irreversível.
  const coverage =
    selectedCarrier !== null
      ? buildCarrierCoverageSelections(selectedCarrier, transport)
      : null;
  const breakdown = computeBreakdown(
    selectedPlacement,
    coverage?.ok === true ? coverage.options : selectedCarrier
  );

  // Lê o estado REAL (status + placement + transporte) numa tacada. null em erro.
  // `silent`: no poll de preparação, uma falha transitória NÃO deve virar tela de
  // erro — apenas devolve null e o laço continua tentando.
  const refresh = useCallback(
    async (silent = false): Promise<Snapshot | null> => {
      try {
        const [statusRes, placementRes, transportRes] = await Promise.all([
          api.getWorkflowStatus(planId),
          api.getPlacementOptions(planId),
          api.getTransportationOptions(planId)
        ]);
        const search = transportRes.data.partneredSearch;
        return {
          status: statusRes,
          placement: placementRes.data.placementOptions,
          selectedPlacementOptionId:
            placementRes.data.selectedPlacementOptionId ?? null,
          transport: transportRes.data.transportationOptions,
          partneredSearching: search?.searching ?? false,
          partneredProbed: search?.probed ?? 0,
          partneredTotal: search?.total ?? 0
        };
      } catch (error: unknown) {
        if (!silent && alive.current) {
          setErro(error instanceof Error ? error.message : "Falha ao carregar as opções de envio.");
          setPhase("error");
        }
        return null;
      }
    },
    [api, planId]
  );

  function applyTransportReady(options: TransportationOption[]): void {
    setTransport(options);
    const cheapest = cheapestPartnered(options);
    const own = selectableOwnCarrierOptions(options);
    // Só é beco sem saída se NÃO houver parceira NEM transportadora própria
    // reconhecível (situação anômala). Caso contrário, o operador escolhe.
    if (cheapest === null && own.length === 0) {
      setPhase("no-carriers");
      return;
    }
    // Pré-seleção: a parceira mais barata (recomendada); se não houver, a 1ª própria.
    const fallback = cheapest ?? own[0] ?? null;
    setSelectedTransportId(
      (prev) => prev ?? fallback?.transportationOptionId ?? null
    );
    setInfo(null);
    setPhase("choose-carrier");
  }

  // Polla até a BUSCA de transporte terminar de verdade (estágio de espera do
  // workflow), não até a primeira lista não-vazia: durante a sonda de placements
  // o store já contém a lista PROVISÓRIA (own-carrier) da placement anterior —
  // concluir por ela congelava a tela anunciando "sem parceira" segundos antes
  // de a parceira (UPS/DPD cotada) chegar. Falha transitória de rede não aborta
  // a busca (refresh silencioso; o laço continua).
  async function pollTransport(): Promise<void> {
    // Orçamento DINÂMICO: cada placement sondada custa um ciclo
    // generate→poll→list na Amazon (~40 s medidos). Um teto fixo desistia no
    // meio da busca em planos com muitas opções de posicionamento — o limite
    // cresce com o total que o backend informa (`partneredSearch.total`, já
    // limitado pelo teto de sondagem do servidor).
    let budget = TRANSPORT_MAX_POLLS;
    // Falhas consecutivas de consulta: sem contá-las, uma queda de rede/sessão
    // ficava indistinguível de "a Amazon ainda está gerando" e o operador
    // esperava 5 min para receber a mensagem errada.
    let falhasSeguidas = 0;
    for (let attempt = 0; attempt < budget && alive.current; attempt += 1) {
      await sleep(POLL_MS);
      if (!alive.current) return;
      const snap = await refresh(true);
      if (!alive.current) return;
      if (snap === null) {
        falhasSeguidas += 1;
        if (falhasSeguidas >= 5) {
          setInfo(
            "Não foi possível consultar o servidor nas últimas tentativas — " +
              "verifique sua conexão e atualize a página."
          );
        }
        continue;
      }
      falhasSeguidas = 0;
      if (snap.partneredTotal > 1) {
        // ~60 s de folga por placement além da primeira.
        budget = TRANSPORT_MAX_POLLS + (snap.partneredTotal - 1) * 20;
      }
      // Espelha o backend: a sonda pode ter re-selecionado outra placement — a
      // taxa exibida (e o aviso "trocamos a opção") devem seguir a EFETIVA.
      setPlacementOptions(snap.placement);
      setEffectivePlacementId(snap.selectedPlacementOptionId);
      const decision = decideTransportPoll({
        workflowStatus: snap.status.workflowStatus,
        workflowStage: snap.status.workflowStage,
        transportCount: snap.transport.length,
        partneredSearching: snap.partneredSearching
      });
      if (decision === "keep-polling" && snap.partneredSearching) {
        // Progresso honesto: o operador vê que ainda há posicionamentos a testar.
        setInfo(
          `Procurando transportadora parceira da Amazon… (posicionamento ` +
            `${Math.min(snap.partneredProbed + 1, snap.partneredTotal)} de ${snap.partneredTotal})`
        );
      }
      switch (decision) {
        case "terminal":
          setInfo(null);
          registrarFalha(snap.status);
          setPhase("terminal");
          return;
        case "confirmed":
          setInfo(null);
          setPhase("confirmed");
          return;
        case "delivery-window":
          setInfo(null);
          setPhase("delivery-window");
          return;
        case "transport-ready":
          applyTransportReady(snap.transport);
          return;
        case "keep-polling":
          break;
      }
    }
    if (alive.current) {
      setInfo(
        "A Amazon ainda está gerando as opções de transporte. Aguarde alguns segundos e atualize a página."
      );
    }
  }

  /** Guarda o motivo REAL da falha (e a saída possível) vindos do backend. */
  function registrarFalha(status: WorkflowStatusInfo): void {
    if (
      typeof status.failureReason === "string" &&
      status.failureReason.length > 0
    ) {
      setFailureReason(status.failureReason);
    }
    setFailureSkus(status.failureSkus ?? []);
    setCanRestart(status.canRestart === true);
  }

  /**
   * Mesma saída do Passo 2: reinicia o plano quando nada foi registrado na
   * Amazon, senão cria um novo envio com os mesmos produtos (sem os recusados).
   */
  async function recuperarPlano(): Promise<void> {
    setRecuperando(true);
    setErroRecuperacao(null);
    try {
      if (canRestart) {
        await api.restartWorkflow(planId);
        window.location.reload();
        return;
      }
      const novo = await api.clonePlan(planId, failureSkus);
      window.location.href = `${window.location.pathname}?planId=${novo.planId}`;
    } catch (error: unknown) {
      setErroRecuperacao(
        error instanceof Error
          ? error.message
          : "Falha ao preparar a correção do plano."
      );
    } finally {
      setRecuperando(false);
    }
  }

  // Envia o placement (idempotente no backend) e aguarda o transporte gerar.
  async function sendPlacement(placementOptionId: string): Promise<void> {
    if (placementSent.current) return;
    placementSent.current = true;
    setErro(null);
    setPhase("generating");
    setInfo("Consultando transportadoras parceiras da Amazon…");
    try {
      await api.selectPlacement(planId, placementOptionId, shipDate);
    } catch (error: unknown) {
      if (!alive.current) return;
      placementSent.current = false;
      setErro(permissionAwareMessage(error, "selecionar o posicionamento"));
      // ANTES: `setPhase("error")` — fase que NENHUM ramo do JSX renderiza, então
      // a tela ficava com o título do card e mais nada: o operador perdia a lista
      // de posicionamentos e o botão, sem forma de tentar de novo. Voltar para
      // "need-placement" mantém o formulário visível (com o erro acima) e o
      // `placementSent` já foi liberado para nova tentativa.
      setPhase("need-placement");
      return;
    }
    await pollTransport();
  }

  // Mapeia o estado real do plano para a fase da tela. Retorna `false` quando o
  // plano ainda está progredindo até a placement (decisão "preparing") — o
  // chamador deve POLLAR; `true` quando alcançou uma fase acionável/terminal.
  function applySnapshot(snap: Snapshot): boolean {
    const decision = decideStep3Phase({
      workflowStatus: snap.status.workflowStatus,
      workflowStage: snap.status.workflowStage,
      transportCount: snap.transport.length,
      placementCount: snap.placement.length,
      partneredSearching: snap.partneredSearching
    });
    switch (decision) {
      case "terminal":
        setInfo(null);
        registrarFalha(snap.status);
        setPhase("terminal");
        return true;
      case "confirmed":
        setPhase("confirmed");
        return true;
      case "delivery-window":
        setPhase("delivery-window");
        return true;
      case "transport-ready":
        applyTransportReady(snap.transport);
        return true;
      case "generating":
        setInfo("Consultando transportadoras parceiras da Amazon…");
        setPhase("generating");
        void pollTransport();
        return true;
      case "need-placement":
        // NÃO auto-envia (mesmo com placement único): o operador precisa antes
        // informar a DATA DE ENVIO, que vira readyToShipWindow.start ao gerar o transporte.
        // Pré-seleciona a opção RECOMENDADA (menor custo líquido / maior desconto),
        // não a primeira da ordem crua da Amazon — essa ordem não tem relação com
        // custo e fixava opções mais caras como padrão.
        setSelectedPlacementId(
          recommendedPlacement(snap.placement)?.placementOptionId ?? null
        );
        setPhase("need-placement");
        return true;
      case "no-placement":
        setErro("Nenhuma opção de posicionamento disponível para este plano.");
        setPhase("error");
        return true;
      case "preparing":
        return false;
    }
  }

  // Polla enquanto o plano ainda está sendo preparado pela Amazon (transição
  // Passo 2 → 3: SET_PACKING_INFORMATION → GENERATE/LIST_PLACEMENT_OPTIONS, que
  // pode levar minutos) até virar acionável (placement/transporte) ou terminal.
  // Substitui o antigo erro fixo "Conclua o Passo 2", que travava a tela UMA vez
  // sem re-checar — o plano ficava pronto e o operador esperava eternamente.
  async function pollUntilReady(): Promise<void> {
    setInfo("Preparando as opções de envio (a Amazon está gerando o posicionamento)…");
    setPhase("preparing");
    for (let attempt = 0; attempt < MAX_POLLS && alive.current; attempt += 1) {
      await sleep(POLL_MS);
      if (!alive.current) return;
      const snap = await refresh(true); // silencioso: erro transitório não trava a tela
      if (!alive.current) return;
      if (snap === null) continue;
      setPlacementOptions(snap.placement);
      setEffectivePlacementId(snap.selectedPlacementOptionId);
      setTransport(snap.transport);
      if (applySnapshot(snap)) return;
    }
    if (alive.current) {
      setErro(
        "O plano ainda está sendo preparado pela Amazon. Aguarde alguns segundos e atualize a página."
      );
      setPhase("error");
    }
  }

  // Boot: decide a fase a partir do estado real do plano; se ainda não está
  // pronto (preparing), entra no poll de preparação em vez de dar erro imediato.
  useEffect(() => {
    let cancelled = false;
    async function boot(): Promise<void> {
      const snap = await refresh();
      if (snap === null || cancelled || !alive.current) return;
      setPlacementOptions(snap.placement);
      setEffectivePlacementId(snap.selectedPlacementOptionId);
      setTransport(snap.transport);
      if (!applySnapshot(snap)) {
        await pollUntilReady();
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
    // Re-dispara só quando o plano muda (boot lê o estado real a cada montagem).
  }, [planId]);

  async function confirmarTransporte(): Promise<void> {
    if (selectedCarrier === null || selectedPlacement === null) return;
    // Cobertura total exigida pela §9.5: um plano com placement dividido tem
    // vários shipments e o backend só aceita a seleção se TODOS forem cobertos
    // pelo MESMO carrier. Enviar uma seleção parcial vira no-op silencioso
    // (TRANSPORTATION_INCOMPLETE_COVERAGE) e a tela ficaria ~2 min no poll.
    const cover = buildCarrierCoverageSelections(selectedCarrier, transport);
    if (!cover.ok) {
      const carrierLabel = carrierDisplayName(selectedCarrier);
      setErro(
        `A transportadora ${carrierLabel} não cobre todos os envios deste plano ` +
          `(${cover.uncoveredShipmentIds.length} sem cobertura: ` +
          `${cover.uncoveredShipmentIds.join(", ")}). Escolha uma transportadora ` +
          `que atenda a todos os envios.`
      );
      return;
    }
    setConfirming(true);
    setErro(null);
    try {
      // adminOverride só para TRANSPORTADORA PRÓPRIA: o backend (§9.5) exige a
      // confirmação explícita do Admin para escolher own-carrier quando há parceira
      // disponível (ex.: Hazmat). Sem parceira, o backend aceita sem override — mas
      // enviar o flag é inócuo e cobre ambos os casos.
      await api.selectTransportation(planId, cover.selections, selectedIsOwn);
    } catch (error: unknown) {
      if (!alive.current) return;
      setErro(permissionAwareMessage(error, "confirmar o transporte"));
      setConfirming(false);
      return;
    }
    setInfo("Confirmando o transporte com a Amazon…");
    for (let attempt = 0; attempt < MAX_POLLS && alive.current; attempt += 1) {
      await sleep(POLL_MS);
      if (!alive.current) return;
      const snap = await refresh();
      if (snap === null || !alive.current) return;
      const stage = snap.status.workflowStage ?? "";
      if (TERMINAL_WORKFLOW_STATUSES.has(snap.status.workflowStatus)) {
        // Mostra a causa REAL (a mesma que o backend gravou) em vez do genérico
        // "verifique o plano", e leva à fase terminal — que agora oferece saída.
        registrarFalha(snap.status);
        setErro(
          snap.status.failureReason !== undefined &&
            snap.status.failureReason.length > 0
            ? `Falha ao confirmar o transporte: ${snap.status.failureReason}`
            : "O workflow falhou ao confirmar o transporte. Verifique o plano."
        );
        setPhase("terminal");
        setConfirming(false);
        setInfo(null);
        return;
      }
      if (stage === "WAIT_FOR_DELIVERY_WINDOWS") {
        setPhase("delivery-window");
        setConfirming(false);
        setInfo(null);
        return;
      }
      if (snap.status.workflowStatus === "COMPLETED" || POST_TRANSPORT_STAGES.has(stage)) {
        setConfirming(false);
        setInfo(null);
        setPhase("confirmed");
        return;
      }
    }
    if (alive.current) {
      setConfirming(false);
      // O texto antigo mandava "clique em Avançar", mas esse botão só habilita
      // quando a fase vira "confirmed" — a instrução era impossível de seguir.
      // Agora aponta a ação que existe de fato nesta tela.
      setInfo(
        "Transporte enviado; a Amazon ainda está finalizando a confirmação. " +
          "Aguarde alguns instantes e clique novamente em \"Confirmar transporte\" " +
          "para verificar (a operação é idempotente — não duplica o envio)."
      );
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {erro !== null ? (
        <p role="alert" style={{ color: "var(--orange)" }}>{erro}</p>
      ) : null}

      {phase === "terminal" ? (
        <Card>
          <p role="alert" style={{ color: "var(--orange)", margin: 0 }}>
            Este plano foi cancelado ou falhou — não é possível confirmar o envio.
          </p>
          <p style={{ color: "var(--text)", margin: "0.5rem 0 0", fontSize: 12.5 }}>
            <strong>Motivo:</strong>{" "}
            {failureReason ??
              "o plano foi cancelado ou a causa não foi registrada. Consulte o histórico do plano ou acione um Admin."}
          </p>
          <div style={{ marginTop: "0.75rem" }}>
            <p
              style={{
                color: "var(--text-muted)",
                margin: "0 0 0.5rem",
                fontSize: 12.5
              }}
            >
              {canRestart
                ? "Nada foi registrado na Amazon: reinicie este mesmo envio e corrija o Passo 1."
                : failureSkus.length > 0
                  ? `Crie um novo envio com os mesmos produtos, sem ${failureSkus.join(", ")}.`
                  : "Crie um novo envio com os mesmos produtos e ajuste o que for necessário no Passo 1."}
            </p>
            <Button onClick={() => void recuperarPlano()} disabled={recuperando}>
              {recuperando
                ? "Preparando…"
                : canRestart
                  ? "Reiniciar envio"
                  : failureSkus.length > 0
                    ? `Criar novo envio sem ${failureSkus.join(", ")}`
                    : "Criar novo envio com estes produtos"}
            </Button>
            {erroRecuperacao !== null ? (
              <p
                role="alert"
                style={{ color: "var(--orange)", margin: "0.5rem 0 0", fontSize: 12.5 }}
              >
                {erroRecuperacao}
              </p>
            ) : null}
          </div>
        </Card>
      ) : null}

      {phase === "delivery-window" ? (
        <Card>
          <p role="alert" style={{ color: "var(--orange)", margin: 0 }}>
            A transportadora escolhida exige uma janela de entrega agendada, ainda
            não suportada nesta versão. Escolha outra transportadora ou conclua o
            agendamento no Seller Central.
          </p>
          {/*
            Sem este botão a tela era um beco sem saída: nenhum outro ramo do JSX
            trata `delivery-window`, então a lista de transportadoras sumia e não
            havia como voltar atrás — só restava abandonar o plano.
          */}
          <div style={{ marginTop: "0.75rem" }}>
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedTransportId(null);
                setAceite(false);
                setErro(null);
                setPhase("choose-carrier");
              }}
            >
              Escolher outra transportadora
            </Button>
          </div>
        </Card>
      ) : null}

      <Card
        title={
          phase === "need-placement"
            ? "Posicionamento (Amazon) — passo anterior à transportadora"
            : "Selecionar transportadora"
        }
      >
        {info !== null ? (
          <p role="status" style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: "0.6rem" }}>
            {info}
          </p>
        ) : null}

        {phase === "loading" || phase === "generating" || phase === "preparing" ? (
          <p role="status">
            {phase === "generating"
              ? "Gerando opções de transporte na Amazon e procurando transportadora parceira (pode testar mais de uma opção de posicionamento)…"
              : phase === "preparing"
                ? "Preparando o envio: a Amazon está gerando as opções de posicionamento. Isso pode levar alguns minutos…"
                : "Carregando opções de envio…"}
          </p>
        ) : null}

        {phase === "need-placement" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
              <strong>Passo 1 — Posicionamento (Amazon).</strong> Escolha como a
              Amazon distribui o estoque pelos centros de destino. Não é a
              transportadora — depois de buscar, você escolhe a transportadora.
            </p>
            {placementOptions.length > 1 &&
            placementOptions.every(
              (o) =>
                placementNetCost(o) ===
                placementNetCost(placementOptions[0] ?? null)
            ) ? (
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
                As opções têm a <strong>mesma taxa</strong> e diferem nos centros de
                destino da Amazon. A oferta de transportadora <strong>parceira</strong>{" "}
                depende dessa escolha — se a opção marcada não tiver parceira, o FiCC
                testa as demais automaticamente.
              </p>
            ) : null}
            {placementOptions.map((opt, i) => {
              const recomendada =
                recommendedPlacement(placementOptions)?.placementOptionId ===
                opt.placementOptionId;
              const taxa = placementFeeAmount(opt);
              const desconto = placementDiscountAmount(opt);
              return (
                <SelectionCard
                  key={opt.placementOptionId}
                  selected={selectedPlacementId === opt.placementOptionId}
                  onSelect={() => setSelectedPlacementId(opt.placementOptionId)}
                  title={`Opção de posicionamento ${i + 1}`}
                  {...(recomendada
                    ? { badge: <Badge tone="orange">PADRÃO FICC</Badge> }
                    : {})}
                >
                  {opt.shipmentIds.length} envio(s) · Taxa de posicionamento:{" "}
                  {formatMoney(taxa, breakdown.currency)}
                  {taxa === 0 ? " (sem custo)" : ""}
                  {desconto > 0 ? (
                    <span style={{ display: "block", marginTop: "0.25rem", color: "var(--green, #2e7d32)" }}>
                      Desconto da Amazon: {formatMoney(desconto, breakdown.currency)}
                      {opt.discounts[0]?.target !== null &&
                      opt.discounts[0]?.target !== undefined
                        ? ` (${opt.discounts[0].target})`
                        : ""}
                    </span>
                  ) : null}
                </SelectionCard>
              );
            })}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem", maxWidth: 220 }}>
              <label htmlFor="ship-date" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                Data de envio (pronto para coleta)
              </label>
              <input
                id="ship-date"
                type="date"
                value={shipDate}
                min={todayIso()}
                onChange={(e) => setShipDate(e.target.value)}
              />
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                Vai na etiqueta da transportadora. Fixada ao buscar as transportadoras.
              </span>
            </div>
            <div>
              <Button
                onClick={() => {
                  if (selectedPlacementId !== null && shipDate.length > 0) {
                    void sendPlacement(selectedPlacementId);
                  }
                }}
                disabled={selectedPlacementId === null || shipDate.length === 0}
              >
                Buscar transportadoras
              </Button>
            </div>
          </div>
        ) : null}

        {phase === "no-carriers" ? (
          <div>
            <p style={{ color: "var(--text-muted)", fontSize: 12.5 }}>
              A Amazon não retornou nenhuma transportadora utilizável para este envio
              {ownCarrierCount(transport) > 0
                ? ` (${ownCarrierCount(transport)} opções, nenhuma reconhecida).`
                : "."}{" "}
              A oferta de transportadora parceira depende do posicionamento
              escolhido — tente outro posicionamento ou verifique a remessa no
              Seller Central.
            </p>
            {/*
              Antes o texto dizia "tente novamente" sem NENHUM controle: recarregar
              a página recaía na mesma fase (mesmo placement, mesmas opções já
              geradas). Este botão devolve o operador ao formulário para escolher
              outro posicionamento — a única retentativa que muda o resultado.
            */}
            <Button
              variant="secondary"
              onClick={() => {
                placementSent.current = false;
                setSelectedTransportId(null);
                setTransport([]);
                setAceite(false);
                setErro(null);
                setInfo(null);
                setPhase("need-placement");
              }}
            >
              Tentar outro posicionamento
            </Button>
          </div>
        ) : null}

        {phase === "choose-carrier" || phase === "confirmed" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {phase === "confirmed" ? (
              <Badge tone="green">✓ Transporte confirmado</Badge>
            ) : null}

            {/* O workflow trocou a opção de posicionamento para achar parceira —
                o operador precisa saber qual está valendo (é a taxa do total). */}
            {placementTrocada && partnered.length > 0 ? (
              <p
                style={{
                  fontSize: 12.5,
                  color: "var(--text-muted)",
                  border: "1px solid var(--border)",
                  borderRadius: 9,
                  padding: "0.6rem",
                  margin: 0
                }}
              >
                A opção de posicionamento marcada não tinha transportadora parceira
                da Amazon. Trocamos para a{" "}
                <strong>
                  opção{" "}
                  {placementOptions.findIndex(
                    (o) => o.placementOptionId === effectivePlacementId
                  ) + 1}
                </strong>{" "}
                ({selectedPlacement?.shipmentIds.length ?? 0} envio(s) · taxa{" "}
                {formatMoney(placementFeeAmount(selectedPlacement), breakdown.currency)}
                ), que tem parceira.
              </p>
            ) : null}

            {/* PARCEIRAS da Amazon — recomendadas (frete cotado e contratado pela Amazon). */}
            {partnered.length > 0 ? (
              <>
                <p style={subHeading}>
                  Transportadoras parceiras da Amazon — recomendadas (frete cotado e
                  contratado pela Amazon)
                </p>
                {partnered.map((opt) => (
                  <SelectionCard
                    key={opt.transportationOptionId}
                    selected={selectedTransportId === opt.transportationOptionId}
                    onSelect={() =>
                      phase === "choose-carrier"
                        ? setSelectedTransportId(opt.transportationOptionId)
                        : undefined
                    }
                    disabled={phase === "confirmed"}
                    title={carrierDisplayName(opt)}
                    badge={<Badge tone="blue">Parceiro Amazon</Badge>}
                  >
                    {shippingModeLabel(opt.shippingMode)}
                    {opt.quote?.cost != null
                      ? ` · ${formatMoney(opt.quote.cost.amount, opt.quote.cost.currencyCode)}`
                      : " · cotação indisponível"}
                  </SelectionCard>
                ))}
              </>
            ) : (
              <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                A Amazon não ofereceu transportadora <strong>parceira</strong> para
                este envio. Escolha uma <strong>transportadora própria</strong>{" "}
                abaixo — você contrata e paga o frete (também é o caminho para itens
                Hazmat).
              </p>
            )}

            {/* PRÓPRIAS — você contrata e paga (Hazmat ou quando não há parceira). */}
            {ownCarriers.length > 0 ? (
              <>
                <p style={subHeading}>
                  Transportadora própria — você contrata e paga (use para Hazmat ou
                  quando não há parceira)
                </p>
                {ownCarriers.map((opt) => (
                  <SelectionCard
                    key={opt.transportationOptionId}
                    selected={selectedTransportId === opt.transportationOptionId}
                    onSelect={() =>
                      phase === "choose-carrier"
                        ? setSelectedTransportId(opt.transportationOptionId)
                        : undefined
                    }
                    disabled={phase === "confirmed"}
                    title={carrierDisplayName(opt)}
                    badge={<Badge tone="amber">Própria</Badge>}
                  >
                    {shippingModeLabel(opt.shippingMode)} · sem cotação da Amazon
                    (você contrata e paga)
                  </SelectionCard>
                ))}
              </>
            ) : null}
          </div>
        ) : null}
      </Card>

      {phase === "choose-carrier" || phase === "confirmed" ? (
        <Card title="Resumo da remessa">
          <Row label="Taxa de posicionamento" value={formatMoney(breakdown.placement, breakdown.currency)} />
          {/* O desconto da Amazon nestas opções costuma ter target "Fulfillment
              Fee Discount": abate a tarifa de fulfillment cobrada depois, por
              unidade vendida — NÃO o frete deste envio. Por isso entra como
              linha informativa e fica FORA do "Total estimado". */}
          {breakdown.placementDiscount > 0 ? (
            <Row
              label="Desconto da Amazon (tarifa de fulfillment)"
              value={`− ${formatMoney(breakdown.placementDiscount, breakdown.currency)}`}
            />
          ) : null}
          <Row
            label={`Transporte${selectedCarrier !== null ? ` (${carrierDisplayName(selectedCarrier)})` : ""}`}
            value={
              breakdown.transport !== null
                ? formatMoney(breakdown.transport, breakdown.currency)
                : selectedIsOwn
                  ? "você contrata e paga"
                  : "—"
            }
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "0.5rem",
              paddingTop: "0.5rem",
              borderTop: "1px solid var(--border-soft)"
            }}
          >
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>Total estimado</span>
            <span style={{ fontSize: 20, fontWeight: 800, color: "var(--orange)" }}>
              {breakdown.total !== null
                ? formatMoney(breakdown.total, breakdown.currency)
                : "—"}
            </span>
          </div>
        </Card>
      ) : null}

      {phase === "choose-carrier" ? (
        <>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
              border: `2px solid ${aceite ? "var(--orange)" : "var(--border)"}`,
              borderRadius: 9,
              padding: "0.75rem",
              cursor: "pointer",
              fontSize: 13
            }}
          >
            <input type="checkbox" checked={aceite} onChange={(e) => setAceite(e.target.checked)} />
            {selectedIsOwn
              ? `Confirmo o envio por ${selectedCarrier !== null ? carrierDisplayName(selectedCarrier) : "transportadora própria"} (transportadora própria — eu contrato e pago o frete).`
              : `Aceito o valor de ${
                  breakdown.total !== null
                    ? formatMoney(breakdown.total, breakdown.currency)
                    : "transporte"
                } e os termos de envio.`}
            {aceite ? <Badge tone="green">aceito</Badge> : null}
          </label>
          <div>
            <Button
              onClick={() => void confirmarTransporte()}
              disabled={!aceite || selectedCarrier === null || confirming}
            >
              {confirming ? "Confirmando…" : "Confirmar transporte"}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function permissionAwareMessage(error: unknown, acao: string): string {
  if (error instanceof ApiFetchError && (error.status === 401 || error.status === 403)) {
    return `Sem permissão para ${acao} (ação restrita a Admin).`;
  }
  return error instanceof Error ? error.message : `Falha ao ${acao}.`;
}

function Row({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5 }}>
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

/** Cabeçalho de subseção (parceiras × próprias) na lista de transportadoras. */
const subHeading = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--text-muted)",
  margin: "0.3rem 0 0.1rem"
} as const;
